import { useEffect } from 'react';
import { updatePageSEO } from '../../utils/seo';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';

export default function TermsConditions() {
  useEffect(() => {
    updatePageSEO({
      title: 'Terms & Conditions - Market Me | Website Design Service Agreement',
      description: 'Read Market Me\'s terms and conditions for our website design and management services. Understand your rights, responsibilities, and our service commitments.',
      keywords: 'terms and conditions, service agreement, website design terms, Market Me terms',
      canonical: 'https://marketme.com/terms-conditions'
    });
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      <Navigation />
      
      <main className="max-w-4xl mx-auto px-4 md:px-8 py-20 md:py-32">
        <h1 className="text-4xl md:text-5xl font-light mb-8 bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
          Terms &amp; Conditions
        </h1>
        <p className="text-white/60 mb-8 text-sm md:text-base">Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>

        <div className="space-y-8 text-white/80 leading-relaxed">
          <section>
            <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">1. Introduction</h2>
            <p className="mb-4">
              These Terms and Conditions ("Terms") govern your use of Market Me's website and services. By accessing our website or engaging our services, you agree to be bound by these Terms.
            </p>
            <p>
              Please read these Terms carefully before using our services. If you do not agree with any part of these Terms, you must not use our services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">2. Definitions</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>"Market Me", "we", "our", "us":</strong> Refers to Market Me and its representatives</li>
              <li><strong>"Client", "you", "your":</strong> Refers to the individual or business engaging our services</li>
              <li><strong>"Services":</strong> All web design, development, management, and related services provided by Market Me</li>
              <li><strong>"Website":</strong> The Market Me website and all associated pages</li>
              <li><strong>"Deliverables":</strong> All work products, designs, code, and materials created for the Client</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">3. Service Scope</h2>
            
            <h3 className="text-xl font-semibold text-white mb-3 mt-6">3.1 Services Offered</h3>
            <p className="mb-4">Market Me provides the following services:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Custom website design and development</li>
              <li>Website launch and deployment</li>
              <li>Conversion optimisation and analytics</li>
              <li>Booking system and payment integration</li>
              <li>Ongoing website management and maintenance</li>
              <li>Technical support and updates</li>
              <li>SEO optimisation and performance monitoring</li>
            </ul>

            <h3 className="text-xl font-semibold text-white mb-3 mt-6">3.2 Service Packages</h3>
            <p className="mb-4">
              Services are provided through subscription packages (Starter, Growth, Premium) as detailed on our website. Each package includes specific features and support levels. Additional services may be available upon request and subject to separate agreement.
            </p>

            <h3 className="text-xl font-semibold text-white mb-3 mt-6">3.3 Service Limitations</h3>
            <p className="mb-4">Our services do not include:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Content creation (copywriting, photography, videography) unless specifically agreed</li>
              <li>Third-party software licences or subscriptions</li>
              <li>Domain registration or hosting fees (unless included in package)</li>
              <li>Services outside the agreed scope without prior written approval</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">4. Client Responsibilities</h2>
            <p className="mb-4">The Client agrees to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide accurate and complete information required for service delivery</li>
              <li>Respond to requests for information and feedback within reasonable timeframes</li>
              <li>Provide necessary access to accounts, platforms, and systems</li>
              <li>Supply content, images, and materials in appropriate formats</li>
              <li>Review and approve deliverables within agreed timeframes</li>
              <li>Ensure all provided content complies with applicable laws and does not infringe third-party rights</li>
              <li>Maintain confidentiality of login credentials and account information</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">5. Payment Terms</h2>
            
            <h3 className="text-xl font-semibold text-white mb-3 mt-6">5.1 Pricing</h3>
            <p className="mb-4">
              Service pricing is as displayed on our website or as agreed in writing. All prices are in GBP (£) and exclude VAT unless stated otherwise. We reserve the right to change our pricing with 30 days' notice to existing clients.
            </p>

            <h3 className="text-xl font-semibold text-white mb-3 mt-6">5.2 Payment Schedule</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Subscription Services:</strong> Billed monthly or annually in advance</li>
              <li><strong>One-off Projects:</strong> 50% deposit upon agreement, 50% upon completion</li>
              <li><strong>Additional Services:</strong> Invoiced separately and payable within 14 days</li>
            </ul>

            <h3 className="text-xl font-semibold text-white mb-3 mt-6">5.3 Payment Methods</h3>
            <p className="mb-4">
              We accept payment via bank transfer, credit/debit card, and other methods as agreed. Payment processing is handled securely through third-party payment providers.
            </p>

            <h3 className="text-xl font-semibold text-white mb-3 mt-6">5.4 Late Payment</h3>
            <p className="mb-4">
              Late payments may incur interest at 8% above the Bank of England base rate per annum. We reserve the right to suspend services for accounts with overdue payments exceeding 14 days.
            </p>

            <h3 className="text-xl font-semibold text-white mb-3 mt-6">5.5 Refunds</h3>
            <p>
              Subscription fees are non-refundable. For one-off projects, deposits are non-refundable once work has commenced. Refunds for completed work are at our sole discretion.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">6. Intellectual Property Rights</h2>
            
            <h3 className="text-xl font-semibold text-white mb-3 mt-6">6.1 Ownership Upon Payment</h3>
            <p className="mb-4">
              Upon receipt of full payment for services, all intellectual property rights in the final deliverables (including designs, code, and content created by Market Me) transfer to the Client. This includes:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Website design and visual elements</li>
              <li>Custom code and functionality</li>
              <li>Graphics and images created specifically for the Client</li>
              <li>Documentation and materials produced for the project</li>
            </ul>

            <h3 className="text-xl font-semibold text-white mb-3 mt-6">6.2 Pre-existing Materials</h3>
            <p className="mb-4">
              Market Me retains ownership of:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Pre-existing code libraries, frameworks, and tools</li>
              <li>Reusable components and templates</li>
              <li>Methodologies and processes</li>
              <li>General knowledge and expertise</li>
            </ul>

            <h3 className="text-xl font-semibold text-white mb-3 mt-6">6.3 Third-Party Materials</h3>
            <p className="mb-4">
              Third-party software, plugins, fonts, stock images, and other licensed materials remain the property of their respective owners and are subject to their licence terms.
            </p>

            <h3 className="text-xl font-semibold text-white mb-3 mt-6">6.4 Portfolio Rights</h3>
            <p>
              Market Me reserves the right to display completed projects in our portfolio, marketing materials, and case studies unless the Client requests confidentiality in writing.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">7. Warranties and Disclaimers</h2>
            
            <h3 className="text-xl font-semibold text-white mb-3 mt-6">7.1 Our Warranties</h3>
            <p className="mb-4">We warrant that:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Services will be performed with reasonable skill and care</li>
              <li>Deliverables will substantially conform to agreed specifications</li>
              <li>We have the right to provide the services and grant the rights specified</li>
              <li>Our work will not knowingly infringe third-party intellectual property rights</li>
            </ul>

            <h3 className="text-xl font-semibold text-white mb-3 mt-6">7.2 Disclaimers</h3>
            <p className="mb-4">
              Except as expressly stated, all services are provided "as is" without warranties of any kind, express or implied. We specifically disclaim:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Warranties of merchantability or fitness for a particular purpose</li>
              <li>Guarantees of specific business results, traffic, or revenue</li>
              <li>Responsibility for third-party services, software, or platforms</li>
              <li>Guarantees of uninterrupted or error-free operation</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">8. Limitation of Liability</h2>
            
            <h3 className="text-xl font-semibold text-white mb-3 mt-6">8.1 Liability Cap</h3>
            <p className="mb-4">
              Our total liability for any claims arising from or related to our services shall not exceed the total fees paid by the Client in the 12 months preceding the claim, or £5,000, whichever is lower.
            </p>

            <h3 className="text-xl font-semibold text-white mb-3 mt-6">8.2 Excluded Damages</h3>
            <p className="mb-4">
              We shall not be liable for:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Loss of profits, revenue, or business opportunities</li>
              <li>Loss of data or information</li>
              <li>Indirect, consequential, or special damages</li>
              <li>Damages resulting from Client's breach of these Terms</li>
              <li>Issues caused by third-party services or platforms</li>
              <li>Damages arising from unauthorised access or security breaches beyond our control</li>
            </ul>

            <h3 className="text-xl font-semibold text-white mb-3 mt-6">8.3 Exceptions</h3>
            <p>
              Nothing in these Terms excludes or limits our liability for death or personal injury caused by negligence, fraud or fraudulent misrepresentation, or any other liability that cannot be excluded by law.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">9. Confidentiality</h2>
            <p className="mb-4">
              Both parties agree to keep confidential all non-public information disclosed during the course of the engagement. This obligation continues for 3 years after termination of services.
            </p>
            <p className="mb-4">Confidential information does not include information that:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Is publicly available through no breach of this agreement</li>
              <li>Was known prior to disclosure</li>
              <li>Is independently developed</li>
              <li>Must be disclosed by law or court order</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">10. Term and Termination</h2>
            
            <h3 className="text-xl font-semibold text-white mb-3 mt-6">10.1 Subscription Services</h3>
            <p className="mb-4">
              Subscription services continue on a rolling basis until terminated by either party with 30 days' written notice. Termination takes effect at the end of the current billing period.
            </p>

            <h3 className="text-xl font-semibold text-white mb-3 mt-6">10.2 One-off Projects</h3>
            <p className="mb-4">
              Project agreements terminate upon completion and final payment, unless ongoing services are included.
            </p>

            <h3 className="text-xl font-semibold text-white mb-3 mt-6">10.3 Termination for Breach</h3>
            <p className="mb-4">
              Either party may terminate immediately if the other party:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Materially breaches these Terms and fails to remedy within 14 days of written notice</li>
              <li>Becomes insolvent or enters bankruptcy proceedings</li>
              <li>Engages in fraudulent or illegal conduct</li>
            </ul>

            <h3 className="text-xl font-semibold text-white mb-3 mt-6">10.4 Effects of Termination</h3>
            <p className="mb-4">Upon termination:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Client must pay all outstanding fees for services rendered</li>
              <li>We will provide reasonable assistance with transition (fees may apply)</li>
              <li>Client retains rights to completed and paid-for deliverables</li>
              <li>Confidentiality obligations continue</li>
              <li>We may retain copies of work for legal and archival purposes</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">11. Force Majeure</h2>
            <p>
              Neither party shall be liable for failure to perform obligations due to circumstances beyond reasonable control, including natural disasters, war, terrorism, strikes, government actions, or technical failures. The affected party must notify the other promptly and make reasonable efforts to resume performance.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">12. Indemnification</h2>
            <p className="mb-4">
              The Client agrees to indemnify and hold Market Me harmless from any claims, damages, or expenses arising from:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Content, materials, or information provided by the Client</li>
              <li>Client's breach of these Terms</li>
              <li>Client's violation of applicable laws or third-party rights</li>
              <li>Client's use of deliverables in a manner not authorised by these Terms</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">13. Data Protection</h2>
            <p>
              Both parties agree to comply with applicable data protection laws, including UK GDPR. Our processing of personal data is governed by our Privacy Policy. Where we process personal data on the Client's behalf, we act as a data processor and will enter into appropriate data processing agreements.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">14. General Provisions</h2>
            
            <h3 className="text-xl font-semibold text-white mb-3 mt-6">14.1 Entire Agreement</h3>
            <p className="mb-4">
              These Terms, together with any written agreements and our Privacy Policy, constitute the entire agreement between the parties and supersede all prior agreements and understandings.
            </p>

            <h3 className="text-xl font-semibold text-white mb-3 mt-6">14.2 Amendments</h3>
            <p className="mb-4">
              We may update these Terms from time to time. Material changes will be notified to active clients with 30 days' notice. Continued use of services after changes constitutes acceptance.
            </p>

            <h3 className="text-xl font-semibold text-white mb-3 mt-6">14.3 Assignment</h3>
            <p className="mb-4">
              The Client may not assign or transfer rights or obligations without our written consent. We may assign our rights and obligations to a successor or affiliate with notice to the Client.
            </p>

            <h3 className="text-xl font-semibold text-white mb-3 mt-6">14.4 Severability</h3>
            <p className="mb-4">
              If any provision is found invalid or unenforceable, the remaining provisions continue in full force and effect.
            </p>

            <h3 className="text-xl font-semibold text-white mb-3 mt-6">14.5 Waiver</h3>
            <p className="mb-4">
              Failure to enforce any provision does not constitute a waiver of that provision or any other provision.
            </p>

            <h3 className="text-xl font-semibold text-white mb-3 mt-6">14.6 Notices</h3>
            <p>
              All notices must be in writing and sent to the email addresses provided. Notices are deemed received when sent by email during business hours, or the next business day if sent outside business hours.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">15. Governing Law and Jurisdiction</h2>
            <p className="mb-4">
              These Terms are governed by the laws of England and Wales. Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.
            </p>
            <p>
              Before initiating legal proceedings, parties agree to attempt to resolve disputes through good faith negotiation for at least 30 days.
            </p>
          </section>

          <section>
            <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">16. Contact Information</h2>
            <p className="mb-4">
              For questions about these Terms, please contact us:
            </p>
            <p>
              <strong>Email:</strong> team.marketme@gmail.com
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
